import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  input,
  model,
  output,
  untracked,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from 'primeng/api';
import { DrawerModule } from 'primeng/drawer';
import { SeahorseTemplateDirective } from '../template/template.directive';

/**
 * A panel that slides in from the side, over the page.
 *
 *   <sh-drawer [(visible)]="open" title="shell.pageHeader.allFiltersTitle">
 *     ...content...
 *     <ng-template shTemplate="footer">
 *       <button>Clear all</button>
 *     </ng-template>
 *   </sh-drawer>
 *
 * `title` is a translation key or plain text. `closed` fires once the drawer
 * has gone (closed by the user, `visible` set to false, or Escape). `width`
 * is any CSS width; `styleClass` is a central class hook.
 */
@Component({
  selector: 'sh-drawer',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, TranslateModule, DrawerModule, SharedModule],
  template: `
    <p-drawer
      [visible]="visible()"
      (visibleChange)="visible.set($event)"
      [position]="position()"
      [header]="title() | translate"
      [styleClass]="styleClass()"
      [style]="widthStyle()"
    >
      <ng-content></ng-content>
      <ng-template pTemplate="footer" *ngIf="footer() as footerTemplate">
        <ng-container *ngTemplateOutlet="footerTemplate"></ng-container>
      </ng-template>
    </p-drawer>
  `,
})
export class ShDrawerComponent {
  readonly visible = model(false);
  readonly position = input<'left' | 'right' | 'top' | 'bottom'>('right');
  /** Translation key or plain text. */
  readonly title = input<string>();
  /** A CSS width, e.g. `'360px'`. */
  readonly width = input<string>();
  /** Central class hook on the drawer (e.g. `page-filters-drawer`). */
  readonly styleClass = input<string>();

  readonly closed = output<void>();

  private readonly _templates = contentChildren(SeahorseTemplateDirective);

  protected readonly footer = computed(
    () =>
      this._templates().find((t) => t.type === 'footer')?.templateRef ??
      undefined
  );
  constructor() {
    // Closed by the user (close button, mask, Escape) or by setting
    // `visible` to false: either way the drawer is gone afterwards.
    let wasOpen = false;
    effect(() => {
      const open = this.visible();
      if (wasOpen && !open) untracked(() => this.closed.emit());
      wasOpen = open;
    });
  }

  protected readonly widthStyle = computed(() =>
    this.width() ? { width: this.width() } : undefined
  );
}
